'use client';

import { useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';
import { Heading } from '../Heading/Heading';
import { Button } from '../Clickables/Buttons/Button';
import { SpeciesGroupCard } from './components/SpeciesGroupCard/SpeciesGroupCard';
import { TopSpeciesCTA } from '../TopSpeciesCTA/TopSpeciesCTA';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { ISpeciesGrouping } from '../../constants/species/types';

const DEFAULT_MAX_VISIBLE_ITEMS = 3;

interface IProps {
  appDownloadModalHeader?: string;
  countryCode: string;
  regionCode: string;
  className?: string;
  id?: string;
  clickEventSourceId?: string;
  headingText: string;
  speciesGroups: ISpeciesGrouping[];
  nrOfVisibleItems?: number;
  hasDownloadCTA?: boolean;
}

export const SpeciesGroupSection = ({
  appDownloadModalHeader = `Download the Fishbrain app to see all species from this area`,
  clickEventSourceId,
  countryCode,
  regionCode,
  id = 'species-group-section',
  headingText,
  speciesGroups,
  className,
  nrOfVisibleItems = DEFAULT_MAX_VISIBLE_ITEMS,
  hasDownloadCTA = true,
}: IProps) => {
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);
  const [visibleTopSpeciesItems, setVisibleTopSpeciesItems] = useState(nrOfVisibleItems);
  const [showDownloadCTA, setShowDownloadCTA] = useState(false);
  const { currentPage } = useAnalytics();

  const speciesCount = speciesGroups.length;
  const hasMoreSpecies = visibleTopSpeciesItems < speciesCount;
  const showMoreSpeciesButton = hasMoreSpecies || (hasDownloadCTA && !showDownloadCTA);

  // Filter duplicates by name
  const uniqueSpeciesGroups = speciesGroups.filter(
    (group, index, self) => self.findIndex(g => g.name === group.name) === index,
  );

  const handleShowMore = () => {
    if (hasMoreSpecies) {
      // Show more species
      setVisibleTopSpeciesItems(prev => Math.min(prev + nrOfVisibleItems, speciesCount));
    } else if (hasDownloadCTA) {
      // Show the CTA if there are no more species and CTA is enabled
      setShowDownloadCTA(true);
    }
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'SpeciesGroupSection',
        source_id: clickEventSourceId,
        type: 'read-more',
        page: currentPage,
      },
    });
  };

  const handleCTAClick = () => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'TopSpeciesSection',
        source_id: clickEventSourceId,
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div
      id={id}
      className={twMerge(
        'sm:bg-lake-fog scroll-mt-(--navigation-scrolltop-offset-mobile) sm:scroll-mt-(--navigation-scrolltop-offset) sm:rounded-2xl',
        className,
      )}
    >
      <Heading className="mb-4 sm:mb-8" variant="sm" as="h2">
        {headingText}
      </Heading>
      <div className="flex flex-col">
        {uniqueSpeciesGroups.map(({ name, externalId, image }, index) => {
          const imageUrl = image.url;
          const imageWidth = image.width;
          const imageHeight = image.height;
          return (
            <SpeciesGroupCard
              key={`species-${externalId}`}
              speciesId={externalId}
              name={name}
              className={`w-full ${index >= visibleTopSpeciesItems ? 'hidden' : ''}`}
              countryCode={countryCode}
              regionCode={regionCode}
              imageUrl={imageUrl}
              imageWidth={imageWidth}
              imageHeight={imageHeight}
              clickEventPosition={index}
              clickEventSourceId={clickEventSourceId}
              clickEventSource="TopSpeciesSection"
              useCustomLoader={false}
            />
          );
        })}
        {showMoreSpeciesButton && (
          <Button size="sm" className="mt-2 p-2" variant="textPrimary" onClick={handleShowMore}>
            Show more species
          </Button>
        )}

        {showDownloadCTA && hasDownloadCTA && (
          <TopSpeciesCTA onCTAClick={handleCTAClick} sourceId={clickEventSourceId} />
        )}
      </div>
      <AppDownloadModal
        appDownloadSource="SpeciesGroupSection"
        appDownloadSourceId={clickEventSourceId}
        modalHeader={appDownloadModalHeader}
        isModalOpen={isAppDownloadModalOpen}
        onCloseModal={() => setIsAppDownloadModalOpen(false)}
      />
    </div>
  );
};
